import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface UseSpeechResult {
  supported: boolean;
  speaking: boolean;
  /** 실제 방송 음원을 재생한다. 음원이 없으면 아무 소리도 내지 않는다. */
  playAnnouncement: (audioUrl?: string, onDone?: () => void) => void;
  /** 재생 배속을 설정한다. (기본 1배속) */
  setRate: (value: number) => void;
  cancel: () => void;
}

/**
 * 실제 방송 녹음 음원만 재생한다. (브라우저 음성 합성 TTS는 사용하지 않는다)
 */
export function useSpeech(): UseSpeechResult {
  const audioSupported =
    typeof window !== "undefined" && typeof Audio !== "undefined";

  const [speaking, setSpeaking] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const tokenRef = useRef(0);
  const rateRef = useRef(1);

  const setRate = useCallback((value: number) => {
    rateRef.current = value;
    if (audioRef.current) audioRef.current.playbackRate = value;
  }, []);

  const stopAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.onended = null;
    audio.onerror = null;
    audio.pause();
    audioRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    tokenRef.current += 1;
    stopAudio();
    setSpeaking(false);
  }, [stopAudio]);

  const playAnnouncement = useCallback(
    (audioUrl?: string, onDone?: () => void) => {
      const token = tokenRef.current + 1;
      tokenRef.current = token;

      stopAudio();

      const finish = () => {
        if (tokenRef.current !== token) return;
        setSpeaking(false);
        onDone?.();
      };

      // 음원이 없으면 아무 소리도 내지 않는다.
      if (!audioUrl || !audioSupported) {
        finish();
        return;
      }

      try {
        const audio = new Audio(audioUrl);
        audio.playbackRate = rateRef.current;
        audioRef.current = audio;
        audio.onplay = () => {
          if (tokenRef.current === token) setSpeaking(true);
        };
        audio.onended = finish;
        audio.onerror = finish;

        const played = audio.play();
        if (played && typeof played.catch === "function") {
          void played.catch(finish);
        }
      } catch {
        finish();
      }
    },
    [audioSupported, stopAudio],
  );

  useEffect(
    () => () => {
      stopAudio();
    },
    [stopAudio],
  );

  return useMemo(
    () => ({
      supported: audioSupported,
      speaking,
      playAnnouncement,
      setRate,
      cancel,
    }),
    [audioSupported, speaking, playAnnouncement, setRate, cancel],
  );
}